import { Box, Card, List, SimpleGrid, Text, ThemeIcon, Title } from '@mantine/core';
import { Controls, ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Component, useMemo } from 'react';
import { diffFlows } from '../../helpers/flowDiff';
import AbSplitNode from '../Flow/AbSplitNode';
import ConditionalNode from '../Flow/ConditionalNode';
import DefaultNode from '../Flow/DefaultNode';
import StartNode from '../Flow/StartNode';
import classes from './FlowProposalDiff.module.css';

const nodeTypes = {
  default: DefaultNode,
  conditional: ConditionalNode,
  abSplit: AbSplitNode,
  start: StartNode,
};

const DIFFERENCE_COLORS = { added: 'green', removed: 'red', changed: 'orange' };

class CanvasErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError?.();
  }

  render() {
    return this.state.failed ? (
      <Text size="sm" c="dimmed">
        This flow could not be displayed.
      </Text>
    ) : (
      this.props.children
    );
  }
}

const FlowCanvas = ({ title, flow, nodeStatus, onError }) => {
  const nodes = useMemo(
    () =>
      flow.nodes.map(({ width: _width, height: _height, measured: _measured, ...node }) => ({
        ...node,
        className: classes[nodeStatus.get(node.id)],
      })),
    [flow.nodes, nodeStatus]
  );

  return (
    <Card withBorder padding="sm" component="section" aria-label={title}>
      <Title order={5} mb="xs">
        {title}
      </Title>
      <Box className={classes.canvas}>
        <CanvasErrorBoundary onError={onError}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={flow.edges}
              nodeTypes={nodeTypes}
              fitView
              minZoom={0.2}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              edgesFocusable={false}
              deleteKeyCode={null}
            >
              <Controls showInteractive={false} />
            </ReactFlow>
          </ReactFlowProvider>
        </CanvasErrorBoundary>
      </Box>
    </Card>
  );
};

const FlowProposalDiffBody = ({ currentFlow, proposedFlow, onProposedFlowError }) => {
  const { nodeStatus, differences } = useMemo(() => diffFlows(currentFlow, proposedFlow), [currentFlow, proposedFlow]);

  return (
    <>
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        <FlowCanvas title="Current flow" flow={currentFlow} nodeStatus={nodeStatus} />
        <FlowCanvas title="Proposed flow" flow={proposedFlow} nodeStatus={nodeStatus} onError={onProposedFlowError} />
      </SimpleGrid>
      <Title order={5} mt="md" mb="xs">
        Differences
      </Title>
      {differences.length === 0 ? (
        <Text size="sm" c="dimmed">
          The proposed flow is the same as the current one.
        </Text>
      ) : (
        <List size="sm" spacing={4}>
          {differences.map(({ kind, text }, index) => (
            <List.Item key={index} icon={<ThemeIcon color={DIFFERENCE_COLORS[kind]} size={10} radius="xl" />}>
              {text}
            </List.Item>
          ))}
        </List>
      )}
    </>
  );
};

const FlowProposalDiff = ({ onProposedFlowError, ...flows }) => (
  <CanvasErrorBoundary onError={onProposedFlowError}>
    <FlowProposalDiffBody {...flows} onProposedFlowError={onProposedFlowError} />
  </CanvasErrorBoundary>
);

export default FlowProposalDiff;
